import { Link } from '@inertiajs/react'
import { Calendar, Pin, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import ReactionBar from '@/components/ReactionBar'

type ProfilePost = {
  id: string
  body: string
  pinned_at: string | null
  edited_at: string | null
  created_at: string
  author: { name: string | null }
  image_url: string | null
  linked_event: {
    id: string
    slug: string
    title: string
    date: string | null
    page_kind: 'event' | 'fundraiser'
  } | null
  reactions_summary: Record<string, number>
  current_user_reaction: string | null
}

type Props = {
  posts: ProfilePost[]
  isOrgMember: boolean
  canReact: boolean
}

// Deterministic SSR/CSR strip — must produce identical output on both sides
// because this card renders on an SSR'd page (OrganizationProfilesController).
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text
}

// Absolute date (no Date.now() — would mismatch between SSR and hydration).
// Treat YYYY-MM-DD as wall-clock (use noon to avoid TZ rollovers); everything
// else as an instant. Without this, `new Date('2026-06-23')` parses as UTC
// midnight and renders as the previous day in any TZ west of UTC.
function formatDate(iso: string): string {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso)
  const d = new Date(isDateOnly ? `${iso}T12:00:00` : iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function LatestUpdatesCard({ posts, isOrgMember, canReact }: Props) {
  if (posts.length === 0 && !isOrgMember) return null

  if (posts.length === 0 && isOrgMember) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Latest updates</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-base text-muted-foreground">
            Share your first update with your followers.
          </p>
          <Button asChild className="mt-4 w-full">
            <Link href="/dashboard/posts/new">
              <Plus className="size-4" />
              New post
            </Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <section id="updates">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Latest updates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {posts.map((post, idx) => {
            const excerpt = truncate(stripHtml(post.body), 200)
            return (
              <div
                key={post.id}
                className={idx > 0 ? 'border-t border-black/5 pt-4' : undefined}
              >
                {post.image_url && (
                  <img
                    src={post.image_url}
                    alt=""
                    loading="lazy"
                    className="mb-3 aspect-[16/9] w-full rounded-lg object-cover ring-1 ring-black/5"
                  />
                )}
                <div className="mb-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                  {post.pinned_at && (
                    <span className="inline-flex items-center gap-1 rounded-md border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      <Pin className="size-2.5" />
                      Pinned
                    </span>
                  )}
                  {post.author.name && (
                    <>
                      <span className="font-medium text-foreground">{post.author.name}</span>
                      <span>·</span>
                    </>
                  )}
                  <span>{formatDate(post.created_at)}</span>
                  {post.edited_at && (
                    <>
                      <span>·</span>
                      <span>edited</span>
                    </>
                  )}
                </div>
                <p className="text-base text-foreground">{excerpt}</p>
                {post.linked_event && (
                  <Link
                    href={`/events/${post.linked_event.slug}`}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm ring-1 ring-black/5 transition hover:bg-muted hover:ring-primary/20"
                  >
                    <Calendar className="size-4 shrink-0 text-primary" />
                    <span className="truncate font-medium text-foreground">{post.linked_event.title}</span>
                    {post.linked_event.date && (
                      <span className="shrink-0 text-muted-foreground">· {formatDate(post.linked_event.date)}</span>
                    )}
                  </Link>
                )}
                <ReactionBar
                  postId={post.id}
                  reactionsSummary={post.reactions_summary}
                  currentUserReaction={post.current_user_reaction}
                  canReact={canReact}
                />
              </div>
            )
          })}
        </CardContent>
      </Card>
      {posts.length === 3 && (
        <div className="mt-2 text-right">
          <a
            href="#updates"
            className="text-sm font-medium text-primary hover:underline"
          >
            View all updates →
          </a>
        </div>
      )}
    </section>
  )
}
